// Global Variable Storage Service for Workflow Execution
class GlobalVariableService {
  constructor() {
    this.variables = {};
    this.listeners = [];
  }

  // Set a variable value
  setVariable(name, value) {
    this.variables[name] = {
      value: value,
      timestamp: new Date().toISOString(),
      type: this.getVariableType(value)
    };
    
    // Notify listeners
    this.notifyListeners('set', name, value);
    
    console.log(`[Global Variables] Set ${name}:`, value);
  }

  // Get a variable value
  getVariable(name) {
    return this.variables[name]?.value;
  }

  // Get variable with metadata
  getVariableDetails(name) {
    return this.variables[name];
  }

  // Get all variables (returns a shallow copy to trigger React re-renders)
  getAllVariables() {
    return { ...this.variables };
  }

  // Get variable names
  getVariableNames() {
    return Object.keys(this.variables);
  }

  // Clear all variables
  clearAll() {
    const oldVariables = { ...this.variables };
    this.variables = {};
    this.notifyListeners('clearAll', null, null, oldVariables);
    console.log('[Global Variables] Cleared all variables');
  }

  // Clear specific variable
  clearVariable(name) {
    if (this.variables[name]) {
      const oldValue = this.variables[name].value;
      delete this.variables[name];
      this.notifyListeners('clear', name, null, oldValue);
      console.log(`[Global Variables] Cleared ${name}`);
    }
  }

  // Set multiple variables at once
  setVariables(variableMap) {
    Object.entries(variableMap).forEach(([name, value]) => {
      this.setVariable(name, value);
    });
  }

  // Check if variable exists
  hasVariable(name) {
    return name in this.variables;
  }

  // Get variable type
  getVariableType(value) {
    if (value === null || value === undefined) return 'null';
    if (Array.isArray(value)) return 'array';
    if (typeof value === 'object') return 'object';
    if (typeof value === 'string') return 'string';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    return 'unknown';
  }

  // Export variables as JSON
  exportVariables() {
    return JSON.stringify(this.variables, null, 2);
  }

  // Import variables from JSON
  importVariables(jsonString) {
    try {
      const imported = JSON.parse(jsonString);
      this.variables = { ...this.variables, ...imported };
      this.notifyListeners('import', null, imported);
      console.log('[Global Variables] Imported variables:', Object.keys(imported));
      return true;
    } catch (error) {
      console.error('[Global Variables] Import failed:', error);
      return false;
    }
  }

  // Add listener for variable changes
  addListener(listener) {
    this.listeners.push(listener);
  }

  // Remove listener
  removeListener(listener) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  // Notify all listeners
  notifyListeners(action, name, value, extra = null) {
    this.listeners.forEach(listener => {
      try {
        listener({ action, name, value, extra, variables: this.variables });
      } catch (error) {
        console.error('[Global Variables] Listener error:', error);
      }
    });
  }

  // Get variable summary for debugging
  getVariableSummary() {
    const summary = {};
    Object.entries(this.variables).forEach(([name, data]) => {
      summary[name] = {
        type: data.type,
        hasValue: data.value !== null && data.value !== undefined,
        timestamp: data.timestamp
      };
    });
    return summary;
  }

  // Validate variable name
  isValidVariableName(name) {
    // Valid variable name: letters, numbers, underscore, no spaces, starts with letter/underscore
    return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name);
  }

  // Get variables by type
  getVariablesByType(type) {
    const result = {};
    Object.entries(this.variables).forEach(([name, data]) => {
      if (data.type === type) {
        result[name] = data.value;
      }
    });
    return result;
  }

  // Search variables by name pattern
  searchVariables(pattern) {
    const regex = new RegExp(pattern, 'i');
    const result = {};
    Object.entries(this.variables).forEach(([name, data]) => {
      if (regex.test(name)) {
        result[name] = data.value;
      }
    });
    return result;
  }
}

// Create singleton instance
const globalVariableService = new GlobalVariableService();

export default globalVariableService;